import React, { useState, useEffect } from "react";
import { useField } from "formik";
import { Layer, Stage, Image, Arrow } from "react-konva";

import footballPitch from "assets/img/football_pitch.jpg";

import { FIELD } from "../constants";

interface MouseData {
  x: number;
  y: number;
}

interface IActionPositionFieldProps {
  width: number;
  height: number;
  className?: string;
}

const roundDouble = (value: number, n: number) => {
  return Math.round(value * Math.pow(10, n)) / Math.pow(10, n);
};

const getOffsetCoordinate = (event: React.MouseEvent) => {
  const rect = event.currentTarget.getBoundingClientRect();
  const offsetX = Math.round(event.clientX - rect.left);
  const offsetY = Math.round(event.clientY - rect.top);
  return [offsetX, offsetY];
};

const isValidCoordinate = (
  x: number,
  y: number,
  width: number,
  height: number
) => {
  if (!width || !height) {
    return false;
  }
  if (!x || !y) {
    return false;
  }
  if (x < 0 || y < 0) {
    return false;
  }

  if (x > 1 || y > 1) {
    return false;
  }

  return true;
};

const PRECISION = 4;

export const ActionPositionField = ({
  width,
  height,
  className,
}: IActionPositionFieldProps) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [fromXField, fromXMeta, { setValue: setFromXValue }] = useField(
    FIELD.fromX
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [fromYField, fromYMeta, { setValue: setFromYValue }] = useField(
    FIELD.fromY
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [toXField, toXMeta, { setValue: setToXValue }] = useField(FIELD.toX);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [toYField, toYMeta, { setValue: setToYValue }] = useField(FIELD.toY);

  const [mouseDown, setMouseDown] = useState<MouseData | null>(null);
  const [mouseUp, setMouseUp] = useState<MouseData | null>(null);
  const [mouseMove, setMouseMove] = useState<MouseData | null>(null);
  const [backgroundImage, setBackgroundImage] = useState<any>(null);

  const handleMouseDown = (event: React.MouseEvent) => {
    const [offsetX, offsetY] = getOffsetCoordinate(event);
    setMouseDown({
      x: offsetX,
      y: offsetY,
    });
    setMouseUp(null);
  };

  const handleMouseUp = (event: React.MouseEvent) => {
    const [offsetX, offsetY] = getOffsetCoordinate(event);
    setMouseUp({
      x: offsetX,
      y: offsetY,
    });

    if (mouseDown) {
      setFromXValue(roundDouble(mouseDown.x / width, PRECISION));
      setFromYValue(roundDouble(mouseDown.y / height, PRECISION));
      setToXValue(roundDouble(offsetX / width, PRECISION));
      setToYValue(roundDouble(offsetY / height, PRECISION));
    }
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    if (!mouseDown && !mouseUp) {
      return;
    }

    const [offsetX, offsetY] = getOffsetCoordinate(event);
    setMouseMove({
      x: offsetX,
      y: offsetY,
    });
  };

  useEffect(() => {
    if (!backgroundImage) {
      const image = new window.Image();
      image.src = footballPitch;
      image.onload = () => {
        setBackgroundImage(image);
      };
    }
    const mouseDownFromProps = isValidCoordinate(
      fromXField.value,
      fromYField.value,
      width,
      height
    )
      ? {
          x: fromXField.value * width,
          y: fromYField.value * height,
        }
      : null;
    setMouseDown(mouseDownFromProps);

    const mouseUpFromProps = isValidCoordinate(
      toXField.value,
      toYField.value,
      width,
      height
    )
      ? {
          x: toXField.value * width,
          y: toYField.value * height,
        }
      : null;
    setMouseUp(mouseUpFromProps);
  }, [
    width,
    height,
    fromXField.value,
    fromYField.value,
    toXField.value,
    toYField.value,
    backgroundImage,
    setBackgroundImage,
  ]);
  const endPos = mouseUp || mouseMove;
  return (
    <div
      className={className}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseMove={handleMouseMove}
    >
      <Stage width={width} height={height}>
        <Layer>
          {backgroundImage && (
            <Image width={width} height={height} image={backgroundImage} />
          )}
        </Layer>
        <Layer>
          {mouseDown && endPos && (
            <Arrow
              points={[mouseDown.x, mouseDown.y, endPos.x, endPos.y]}
              pointerLength={10}
              pointerWidth={10}
              fill="red"
              stroke="red"
              strokeWidth={4}
            />
          )}
        </Layer>
      </Stage>
    </div>
  );
};
